import { addMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { SurveyHistoryItem } from '@/api/communication/surveys/get-surveys'
import { Badge, BadgeColorKeys } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SelectOption } from '@/components/ui/select'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import {
  getSurveyStatusColor,
  SurveyCommunicationType,
  SurveyStatus,
  SurveyStatusEnum,
} from '@/models'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'

// Note: Extend possible statuses to include 'All' which will be only indicated in UI
export const SurveyStatusFilterEnum = z.enum([...SurveyStatusEnum.options, 'All'])
export type SurveyStatusFilter = z.infer<typeof SurveyStatusFilterEnum>

export const surveyFrequencyEnum = z.enum(['oneTime', 'ongoing'])

export type SurveyFrequency = z.infer<typeof surveyFrequencyEnum>

export const surveysTableFiltersSchema = z.object({
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  status: SurveyStatusFilterEnum.optional(),
  toDate: z.coerce.number().optional(),
})

export type SurveysTableFilters = z.infer<typeof surveysTableFiltersSchema>

export const getStatusTabOptions = (
  t: TFunction<['communication']>,
): SelectOption<SurveyStatusFilter>[] => [
  { label: t('communication:surveys.list.status.all'), value: SurveyStatusFilterEnum.enum.All },
  {
    label: t('communication:surveys.list.status.scheduled'),
    value: SurveyStatusFilterEnum.enum.Scheduled,
  },
  {
    label: t('communication:surveys.list.status.ongoing'),
    value: SurveyStatusFilterEnum.enum.Ongoing,
  },
  {
    label: t('communication:surveys.list.status.onHold'),
    value: SurveyStatusFilterEnum.enum['On hold'],
  },
  {
    label: t('communication:surveys.list.status.completed'),
    value: SurveyStatusFilterEnum.enum.Completed,
  },
]

export const surveysTableInitialFilters: SurveysTableFilters = {
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  status: SurveyStatusFilterEnum.enum.All,
  toDate: undefined,
}

export const STATUS_COLOR_MAP: Record<SurveyStatus, BadgeColorKeys> = {
  [SurveyStatusEnum.enum.Completed]: 'blue',
  [SurveyStatusEnum.enum['On hold']]: 'orange',
  [SurveyStatusEnum.enum.Ongoing]: 'green',
  [SurveyStatusEnum.enum.Scheduled]: 'purple',
}

export const getChannelLabel = (
  type: SurveyCommunicationType,
  t: TFunction<['communication']>,
): string => {
  const CHANNEL_LABEL_MAP: Record<SurveyCommunicationType, string> = {
    email: t('communication:surveys.list.channel.options.email'),
    'in-app': t('communication:surveys.list.channel.options.inApp'),
    sms: t('communication:surveys.list.channel.options.sms'),
  }

  return CHANNEL_LABEL_MAP[type] ?? type
}

export const SURVEY_ANALYTICS_STORAGE_KEY = 'surveys-analytics-open'

export const SURVEYS_ID = 'surveys-table'

export const getSurveysTableColumns = (
  t: TFunction<['communication']>,
  timezone: string,
): TableColumn<SurveyHistoryItem>[] => [
  {
    id: 'title',
    render: (survey) => (
      <Typography color="black800" size="text-small" weight="medium" breakLines={1}>
        {survey.name}
      </Typography>
    ),
    title: t('communication:surveys.list.columns.title'),
    width: '20rem',
  },
  {
    id: 'channel',
    render: (survey) => (
      <Typography color="black500" size="text-small" weight="medium">
        {getChannelLabel(survey.communicationType, t)}
      </Typography>
    ),
    title: t('communication:surveys.list.columns.channel'),
    width: '12rem',
  },
  {
    id: 'surveyType',
    render: (survey) => (
      <div>
        <Typography color="black800" size="text-small" weight="medium">
          {survey.type}
        </Typography>
        {survey.subtype && (
          <Typography color="black500" size="text-small" weight="medium">
            {survey.subtype}
          </Typography>
        )}
      </div>
    ),
    title: t('communication:surveys.list.columns.surveyType'),
    width: '15rem',
  },
  {
    id: 'delivery',
    render: (survey) => (
      <Typography color="black500" size="text-small" weight="medium">
        {survey.isOneTime
          ? t('communication:surveys.list.delivery.oneTime')
          : t('communication:surveys.list.delivery.ongoing')}
      </Typography>
    ),
    title: t('communication:surveys.list.columns.delivery'),
    width: '12rem',
  },
  {
    id: 'startDate',
    render: (survey) => {
      if (!survey.startDate) return <Typography size="text-small">-</Typography>

      const zonedStartDate = toZonedTime(new Date(survey.startDate), timezone)
      const startWithTime =
        survey.startTime != null ? addMinutes(zonedStartDate, survey.startTime) : null

      return `${formatDateOnly(zonedStartDate)}${startWithTime ? `\n${formatTimeOnly(startWithTime)}` : ''}`
    },
    title: t('communication:surveys.list.columns.startDate'),
    width: '12rem',
  },
  {
    id: 'endDate',
    render: (survey) => {
      if (!survey.endDate) return EMPTY_VALUE
      return formatDateOnly(toZonedTime(new Date(survey.endDate), timezone))
    },
    title: t('communication:surveys.list.columns.endDate'),
    width: '12rem',
  },
  {
    id: 'status',
    render: (survey) => (
      <Badge color={getSurveyStatusColor(survey.status)} size="small" variant="light">
        {survey.status}
      </Badge>
    ),
    title: t('communication:surveys.list.columns.status'),
    width: '12rem',
  },
]

export type { SurveyCommunicationType, SurveyStatus }
